/**
 * Admin Portal → Settings → Notification system (Notification System v2 §12.3):
 * notification retention (1-30 days) and system failure incidents, plus the
 * outage warning from the independent watchdog (§8.4).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, InputNumber, Segmented, Space, Table, Tag, Typography, message } from 'antd';
import { apiRequest } from './index';
import { formatWhen } from './notifyUi';

const { Text } = Typography;

export function NotificationHealthBanner() {
  const [health, setHealth] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => apiRequest('get', 'v2/admin/notifications-v2/health').then((res) => {
      if (alive && res && !res.error) setHealth(res.data);
    });
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  if (!health || !health.enabled) return null;
  if (health.healthy && !health.warning) return null;
  return (
    <Alert
      type={health.healthy ? 'warning' : 'error'}
      showIcon
      style={{ marginBottom: 12 }}
      message={health.healthy ? 'Notification outage alerts are not configured' : 'The notification system needs attention'}
      description={(
        <div>
          {(health.issues || []).map((i) => <div key={i}>{i}</div>)}
          {health.warning ? <div>{health.warning}</div> : null}
        </div>
      )}
    />
  );
}

/**
 * Browser push diagnostics (test report 19 Oct 2026 §4): whether the server can
 * send pushes at all, how many browsers are registered, what is queued and why
 * the last day's deliveries were skipped. Read-only.
 */
const SKIP_REASON_TEXT = {
  push_not_configured: 'server push keys missing',
  push_off: 'person switched browser notifications off',
  no_subscription: 'no registered browser (permission not granted, or never subscribed)',
  ineligible: 'person no longer eligible',
  deleted: 'notification deleted first',
};

export function BrowserPushDiagnostics() {
  const [health, setHealth] = useState(null);
  const load = useCallback(() => apiRequest('get', 'v2/admin/notifications-v2/health').then((res) => {
    if (res && !res.error) setHealth(res.data);
  }), []);
  useEffect(() => { load(); }, [load]);
  if (!health || !health.enabled || !health.web_push) return null;
  const p = health.web_push;
  const skipped = Object.entries(p.skipped_24h || {});
  return (
    <Card title="Browser push" style={{ marginBottom: 16 }} extra={<Button size="small" onClick={load}>Refresh</Button>}>
      <Space direction="vertical" size={4}>
        <span>
          Server keys: {p.configured ? <Tag color="green">configured</Tag> : <Tag color="red">missing</Tag>}
          {!p.configured ? <Text type="secondary"> Set NOTIFY_VAPID_PUBLIC_KEY and NOTIFY_VAPID_PRIVATE_KEY on the API server (scripts/notify_vapid_keys.php), then restart the notification worker.</Text> : null}
        </span>
        <span>Registered browsers: <Text strong>{p.subscriptions}</Text></span>
        <span>Queued now: <Text strong>{p.queued}</Text> · Sent in the last 24 h: <Text strong>{p.sent_24h}</Text> · Failed in the last 24 h: <Text strong>{p.failed_24h}</Text></span>
        {skipped.length ? (
          <span>
            Not pushed in the last 24 h:{' '}
            {skipped.map(([reason, n]) => <Tag key={reason}>{n} × {SKIP_REASON_TEXT[reason] || reason}</Tag>)}
          </span>
        ) : null}
        <Text type="secondary">
          A browser notification is only sent when the person has no visible tab of this portal open, has browser notifications switched on,
          and has allowed notifications in that browser. The Preferences panel on the Notifications page shows each person&apos;s own status and a test button.
        </Text>
      </Space>
    </Card>
  );
}

export default function NotificationSystemPage() {
  const [retention, setRetention] = useState(30);
  const [savingRetention, setSavingRetention] = useState(false);
  const [status, setStatus] = useState('open');
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadSettings = useCallback(async () => {
    const res = await apiRequest('get', 'v2/admin/notifications-v2/settings');
    if (res && !res.error) setRetention(Number(res.data.retention_days.value));
  }, []);

  const loadIncidents = useCallback(async () => {
    setLoading(true);
    const res = await apiRequest('get', 'v2/admin/notifications-v2/incidents', { status: status === 'all' ? undefined : status });
    setIncidents(res && !res.error ? res.data.items : []);
    setLoading(false);
  }, [status]);

  useEffect(() => { loadSettings(); }, [loadSettings]);
  useEffect(() => { loadIncidents(); }, [loadIncidents]);

  const saveRetention = async () => {
    setSavingRetention(true);
    const res = await apiRequest('put', 'v2/admin/notifications-v2/settings', { retention_days: retention });
    setSavingRetention(false);
    if (res && !res.error) message.success('Retention saved. It applies on the next nightly cleanup.');
    else message.error(res?.message || 'Could not save retention');
  };

  const mark = async (id, action) => {
    const res = await apiRequest('post', `v2/admin/notifications-v2/incidents/${id}/${action}`, {});
    if (res && !res.error) {
      message.success(action === 'resolve' ? 'Marked Resolved. Alerts for this incident stop.' : 'Marked Not Resolved. The 24-hour alert continues.');
      loadIncidents();
    } else {
      message.error(res?.message || 'Could not update the incident');
    }
  };

  const columns = [
    { title: 'Operation', dataIndex: 'operation_label', render: (v, r) => <div><div>{v}</div><Text type="secondary">{r.affected_record}</Text></div> },
    { title: 'Reference', dataIndex: 'error_ref', render: (v) => <Text code copyable>{v}</Text> },
    { title: 'First seen', dataIndex: 'first_seen_at', render: formatWhen },
    { title: 'Last seen', dataIndex: 'last_seen_at', render: formatWhen },
    { title: 'Occurrences', dataIndex: 'occurrence_count', align: 'right' },
    { title: 'Alerts sent', dataIndex: 'alert_sequence', align: 'right' },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (v, r) => (v === 'open'
        ? <Space direction="vertical" size={0}><Tag color="red">Open</Tag>{r.next_alert_at ? <Text type="secondary">Next alert {formatWhen(r.next_alert_at)}</Text> : null}{r.last_marking === 'not_resolved' ? <Text type="secondary">Marked Not Resolved</Text> : null}</Space>
        : <Space direction="vertical" size={0}><Tag color="green">Resolved</Tag><Text type="secondary">{formatWhen(r.resolved_at)}</Text></Space>),
    },
    {
      title: 'Actions',
      render: (_, r) => (r.status === 'open'
        ? (
          <Space>
            <Button size="small" type="primary" onClick={() => mark(r.id, 'resolve')}>Resolved</Button>
            <Button size="small" onClick={() => mark(r.id, 'not-resolved')}>Not Resolved</Button>
          </Space>
        )
        : null),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <NotificationHealthBanner />
      <BrowserPushDiagnostics />
      <Card title="Notification retention" style={{ marginBottom: 16 }}>
        <Space wrap>
          <span>Delete notifications older than</span>
          <InputNumber min={1} max={30} value={retention} onChange={(v) => setRetention(v)} aria-label="Retention in days" />
          <span>days (maximum 30).</span>
          <Button type="primary" loading={savingRetention} onClick={saveRetention}>Save</Button>
        </Space>
        <div style={{ marginTop: 8 }}>
          <Text type="secondary">
            Applies to every user&apos;s notification centre on the next nightly cleanup. Unread notifications are deleted too.
            The Admin Portal log is kept separately and is not affected.
          </Text>
        </div>
      </Card>
      <Card
        title="System failure incidents"
        extra={<Segmented value={status} onChange={setStatus} options={[{ label: 'Open', value: 'open' }, { label: 'Resolved', value: 'resolved' }, { label: 'All', value: 'all' }]} />}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          A failure alerts Admin at once and again every 24 hours until it is marked Resolved. A later successful attempt does not resolve it.
        </Text>
        <Table rowKey="id" size="middle" loading={loading} dataSource={incidents} columns={columns} pagination={{ pageSize: 20 }} scroll={{ x: true }} />
      </Card>
    </div>
  );
}
